export interface CaptureDraftV1 {
  version: 1;
  kind: 'voice' | 'listener_personalization';
  voiceId: string | null;
  listenerId?: string;
  persistedSampleIndexes: number[];
  persistedVerbatimKeys: Array<'intro' | 'name' | 'goodnight'>;
  updatedAt: string;
}

export const CAPTURE_DRAFT_PREFIX = 'luma:capture-draft:v1:';

export function captureDraftKey(draftScopeKey: string, voiceId?: string | null, listenerId?: string | null): string {
  // Preserve every existing account key. An accountless listener has no
  // internal voice ID, so its public invitation-listener ID is namespaced
  // explicitly below the opaque session-derived scope.
  const identity = voiceId
    ? `:${voiceId}${listenerId ? `:${listenerId}` : ''}`
    : listenerId ? `:listener:${listenerId}` : '';
  return `${CAPTURE_DRAFT_PREFIX}${draftScopeKey}${identity}`;
}

export function readCaptureDraft(draftScopeKey: string, voiceId?: string | null, listenerId?: string | null): CaptureDraftV1 | null {
  const key = captureDraftKey(draftScopeKey, voiceId, listenerId);
  const discard = () => {
    try { sessionStorage.removeItem(key); } catch { /* no-op */ }
  };
  try {
    const parsed = JSON.parse(sessionStorage.getItem(key) || 'null') as CaptureDraftV1 | null;
    const validClipKeys = new Set(['intro', 'name', 'goodnight']);
    if (!parsed) return null;
    if (parsed.version !== 1 || (parsed.voiceId !== null && typeof parsed.voiceId !== 'string') || (voiceId && parsed.voiceId !== voiceId) || (!voiceId && parsed.voiceId !== null) || !Array.isArray(parsed.persistedSampleIndexes) || !parsed.persistedSampleIndexes.every((index) => Number.isInteger(index) && index >= 1 && index <= 6) || !Array.isArray(parsed.persistedVerbatimKeys) || !parsed.persistedVerbatimKeys.every((clipKey) => validClipKeys.has(clipKey)) || (parsed.listenerId != null && typeof parsed.listenerId !== 'string')) {
      discard();
      return null;
    }
    const updatedAt = Date.parse(parsed.updatedAt);
    if (!Number.isFinite(updatedAt) || Date.now() - updatedAt > 24 * 60 * 60 * 1000 || updatedAt > Date.now() + 5 * 60 * 1000 || (listenerId && parsed.listenerId !== listenerId)) {
      discard();
      return null;
    }
    return parsed;
  } catch {
    discard();
    return null;
  }
}

export function writeCaptureDraft(draftScopeKey: string, draft: CaptureDraftV1): void {
  try { sessionStorage.setItem(captureDraftKey(draftScopeKey, draft.voiceId, draft.listenerId), JSON.stringify(draft)); } catch { /* no-op */ }
}

export function clearCaptureDraft(draftScopeKey: string, voiceId?: string | null, listenerId?: string | null): void {
  try { sessionStorage.removeItem(captureDraftKey(draftScopeKey, voiceId, listenerId)); } catch { /* no-op */ }
}
